import { aliasClassLabel, defaultClassName } from '../browser/siblingAliasDisplay.js';
import { isValidVariableName } from './globalVarValue.js';

const METHOD_TYPES = new Set(['FlowHeader', 'APIHeader']);

function asText(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function normalizePath(value) {
    return asText(value).replace(/\\/g, '/').replace(/\/+$/, '');
}

function catalogName(item) {
    if (typeof item === 'string')
        return asText(item);
    if (!item || typeof item !== 'object')
        return '';
    return asText(item.name) || asText(item.Name);
}

function copyParameters(list) {
    if (!Array.isArray(list))
        return null;
    return list.filter((row) => row).map((row) => ({
        Name: asText(row.Name),
        Type: asText(row.Type),
        Description: asText(row.Description),
        Out: !!row.Out,
        DefaultValue: row.DefaultValue == null ? '' : String(row.DefaultValue),
    }));
}

/**
 * Events for a channel, from that channel's FlowEvents.
 * An entry with neither ReturnType nor Parameters has no signature yet.
 */
export function eventCatalog({ channel } = {}) {
    const raw = channel?.FlowEvents;
    const entries = [];
    const seen = new Set();
    if (!Array.isArray(raw))
        return entries;
    for (const item of raw) {
        const name = catalogName(item);
        if (!name || seen.has(name))
            continue;
        seen.add(name);
        const hasReturn = !!(item && Object.prototype.hasOwnProperty.call(item, 'ReturnType') && asText(item.ReturnType));
        const parameters = copyParameters(item?.Parameters);
        entries.push({
            name,
            description: asText(item?.Description),
            returnType: hasReturn ? asText(item.ReturnType) : '',
            parameters,
            specified: hasReturn || parameters != null,
        });
    }
    return entries;
}

export function catalogByName(catalog) {
    const map = new Map();
    for (const entry of catalog || []) {
        const name = catalogName(entry);
        if (name && !map.has(name))
            map.set(name, typeof entry === 'string' ? { name, specified: false, parameters: null, returnType: '' } : entry);
    }
    return map;
}

/**
 * Short reason when a wired method does not match a specified event signature.
 * Unspecified events, and methods whose header did not include a signature, do not warn.
 */
export function signatureWarning(eventEntry, method) {
    if (!eventEntry?.specified || method?.missing || method?.signatureKnown === false)
        return '';
    const reasons = [];
    const expectedReturn = asText(eventEntry.returnType);
    const actualReturn = asText(method?.returnType);
    if (expectedReturn !== actualReturn)
        reasons.push('Return type');
    const expected = eventEntry.parameters || [];
    const actual = Array.isArray(method?.parameters) ? method.parameters : [];
    const paramsDiffer = expected.length !== actual.length || expected.some((row, index) => {
        const other = actual[index] || {};
        return asText(row.Name) !== asText(other.Name)
            || asText(row.Type) !== asText(other.Type)
            || !!row.Out !== !!other.Out;
    });
    if (paramsDiffer)
        reasons.push('Parameters');
    if (!reasons.length)
        return '';
    return reasons.join(' and ');
}

/** Catalog names, then unsaved custom names, then saved names the catalog does not list. */
export function visibleEventNames(catalog, events, extraNames) {
    const names = [];
    const seen = new Set();
    const push = (value) => {
        const name = catalogName(value);
        if (!name || seen.has(name))
            return;
        seen.add(name);
        names.push(name);
    };
    for (const entry of catalog || [])
        push(entry);
    for (const name of extraNames || [])
        push(name);
    for (const row of events || [])
        push(row?.EventName);
    return names;
}

function methodBaseName(eventName) {
    const raw = asText(eventName);
    if (isValidVariableName(raw))
        return raw;
    const stripped = raw.replace(/[^A-Za-z0-9_]/g, '');
    if (isValidVariableName(stripped))
        return stripped;
    return 'Handler';
}

/** Event name when it is a valid unused method name; otherwise a numeric suffix. */
export function methodNameForEvent(eventName, existingNames) {
    const taken = new Set((existingNames || []).map((name) => asText(name)).filter(Boolean));
    const base = methodBaseName(eventName);
    if (!taken.has(base))
        return base;
    let suffix = 2;
    while (taken.has(`${base}${suffix}`))
        suffix += 1;
    return `${base}${suffix}`;
}

/** Signature copied onto a new flow method. Unspecified events get no parameters and void. */
export function signatureForNewMethod(eventEntry) {
    if (!eventEntry?.specified) {
        return { Parameters: [], ReturnType: 'void' };
    }
    return {
        Parameters: (eventEntry.parameters || []).map((row) => ({
            Name: asText(row.Name),
            Type: asText(row.Type) || 'string',
            Description: asText(row.Description),
            Out: !!row.Out,
            DefaultValue: row.DefaultValue == null ? '' : String(row.DefaultValue),
        })),
        ReturnType: asText(eventEntry.returnType) || 'void',
    };
}

export function newMethodDraft({ eventName, classEntry, existingNames, catalogEntry } = {}) {
    const signature = signatureForNewMethod(catalogEntry);
    return {
        Name: methodNameForEvent(eventName, existingNames),
        Path: asText(classEntry?.methodPath),
        className: asText(classEntry?.name),
        Type: 'FlowHeader',
        Parameters: signature.Parameters,
        ReturnType: signature.ReturnType,
    };
}

export function projectFullPath(project) {
    const name = asText(project?.Name);
    const path = normalizePath(project?.Path);
    if (!name)
        return path;
    return path ? `${path}/${name}` : name;
}

export function pathIsUnderProject(itemPath, projectFull) {
    const path = normalizePath(itemPath);
    const full = normalizePath(projectFull);
    if (!path || !full)
        return false;
    return path === full || path.startsWith(`${full}/`);
}

function classNameForMethod(item, projectFull, projectName) {
    const path = normalizePath(item?.Path);
    const fallback = defaultClassName(projectName) || 'Default';
    if (!path || path === projectFull)
        return fallback;
    const rest = path.startsWith(`${projectFull}/`) ? path.slice(projectFull.length + 1) : path;
    const segment = rest.split('/').filter(Boolean).pop();
    return segment || fallback;
}

function methodSignatureFromItem(item) {
    const known = !!item && (
        Object.prototype.hasOwnProperty.call(item, 'Parameters')
        || Object.prototype.hasOwnProperty.call(item, 'ReturnType')
    );
    return {
        parameters: known ? (copyParameters(item.Parameters) || []) : null,
        returnType: known ? asText(item.ReturnType) : '',
        signatureKnown: known,
    };
}

function ensureClass(classes, projectName, className, projectFull) {
    let entry = classes.get(className);
    if (entry)
        return entry;
    const generatedDefault = defaultClassName(projectName);
    const isDefault = !!generatedDefault && className === generatedDefault;
    entry = {
        name: className,
        label: aliasClassLabel(projectName, className) || className,
        isDefault,
        methodPath: isDefault ? projectFull : (projectFull ? `${projectFull}/${className}` : className),
        methods: [],
    };
    classes.set(className, entry);
    return entry;
}

/**
 * Project, classes, and methods under this project, plus saved handlers that are not.
 * Outside rows are visible so existing links can draw; they are not drop targets.
 */
export function buildMemberTree(folderItems, project, events) {
    const projectName = asText(project?.Name);
    const full = projectFullPath(project);
    const classes = new Map();
    const inProject = new Set();
    const generatedDefault = defaultClassName(projectName);
    if (generatedDefault)
        ensureClass(classes, projectName, generatedDefault, full);

    for (const item of folderItems || []) {
        if (!item || !pathIsUnderProject(item.Path, full))
            continue;
        if (item.Type === 'Class' && asText(item.Name))
            ensureClass(classes, projectName, asText(item.Name), full);
        if (!METHOD_TYPES.has(item.Type) || !asText(item.ID))
            continue;
        const bucket = ensureClass(classes, projectName, classNameForMethod(item, full, projectName), full);
        if (inProject.has(item.ID))
            continue;
        inProject.add(item.ID);
        bucket.methods.push({
            id: item.ID,
            name: asText(item.Name) || item.ID,
            type: item.Type,
            path: normalizePath(item.Path),
            dropTarget: true,
            missing: false,
            ...methodSignatureFromItem(item),
        });
    }

    const classList = [...classes.values()];
    classList.sort((a, b) => {
        if (a.isDefault !== b.isDefault)
            return a.isDefault ? -1 : 1;
        return a.label.localeCompare(b.label);
    });
    for (const entry of classList)
        entry.methods.sort((a, b) => a.name.localeCompare(b.name));

    const outside = [];
    const seenOutside = new Set();
    for (const row of events || []) {
        const id = asText(row?.FlowID);
        if (!id || inProject.has(id) || seenOutside.has(id))
            continue;
        seenOutside.add(id);
        const hit = (folderItems || []).find((item) => item && item.ID === id);
        if (hit) {
            outside.push({
                id,
                name: asText(hit.Name) || id,
                type: hit.Type || '',
                path: normalizePath(hit.Path),
                dropTarget: false,
                missing: false,
                ...methodSignatureFromItem(hit),
            });
        }
        else {
            outside.push({
                id,
                name: id,
                type: '',
                path: '',
                dropTarget: false,
                missing: true,
                parameters: null,
                returnType: '',
                signatureKnown: false,
            });
        }
    }

    return {
        projectName,
        projectPath: full,
        classes: classList,
        outside,
    };
}

function completeRows(events) {
    if (!Array.isArray(events))
        return [];
    return events.filter((row) => row && asText(row.EventName) && asText(row.FlowID));
}

/** One row per event name. A new target replaces the previous method. */
export function connect(events, eventName, flowId) {
    const name = asText(eventName);
    const id = asText(flowId);
    const list = completeRows(events);
    if (!name || !id)
        return list;
    return [
        ...list.filter((row) => row.EventName !== name),
        { EventName: name, FlowID: id },
    ];
}

export function disconnect(events, eventName) {
    const name = asText(eventName);
    return completeRows(events).filter((row) => row.EventName !== name);
}
